// @flow
import React from 'react';
import { createUseStyles } from 'react-jss';
import UnderlinedButton from 'shared/components/UnderlinedButton';
import StyledButton from 'shared/components/StyledButton';
import type { Image } from './SingleImage';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

type Props = {
  image: Image
}

export default function CTABlock(props: Props){
  const { image } = props;
  const {
    color_theme: colorTheme = 'DARK',
    title = '',
    buttons = [],
    button_type: buttonType = 'UNDERLINE',
    description = []
  } = image;
  const c: { [key: string]: string } = useStyles(image);

  // Prevent empty component from blocking user interaction
  if (!title && !buttons.length && !description.length) {
    return null;
  }

  return (
    <div className={c.cta}>
      <div className={c.title}>
        {title}
      </div>
      <div className={c.description}>
        {description.map((t, i) => (<div key={i}>{t.text}</div>))}
      </div>
      <div className={c.btnRow}>
        {
          buttons && buttons.map(btn => (buttonType === 'UNDERLINE' ?
            <UnderlinedButton
              colorTheme={colorTheme}
              label={btn.label}
              url={btn.url}
              key={btn.label}
              openInNewTab={btn.open_in_new_tab}
            /> : <StyledButton
              label={btn.label}
              url={btn.url}
              key={btn.label}
              openInNewTab={btn.open_in_new_tab}
            />
          ))}
      </div>
    </div>
  );
}

const useStyles = createUseStyles({
  cta: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    top: ({ content_padding_top }) => content_padding_top ? content_padding_top : '30%',
    left: '0',
    right: '0',
    alignItems: 'center'
  },
  btnRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '30px',
    '&> *': {
      marginRight: '5px',
      marginLeft: '5px'
    }
  },
  title: {
    color: ({ color_theme }) => color_theme === 'LIGHT' ? 'black' : 'white', // default 'DARK'
    fontFamily: ({ title_font }) => title_font ? title_font : FONT_FAMILY_ROBOTO,
    fontSize: ({ title_font_size }) => `${title_font_size || '60'}px`,
    padding: ({ title_padding_left_right }) => `0px ${title_padding_left_right || '0px'}`,
    textAlign: ({ title_position }) => title_position || 'center',
    opacity: ({ title_opacity }) => title_opacity || '1',
    lineHeight: ({ title_line_height }) => title_line_height || 'normal',
    fontWeight: ({ title_font_weight }) => title_font_weight || '400',
    width: '100%',
    alignItems: 'center',
    letterSpacing: '1.5px',
    whiteSpace: 'pre-line',
    transition: 'all .6s ease-in-out',
    transitionDelay: '.4s',
    '@media(max-width: 960px)': {
      fontSize: '28px'
    }
  },
  description: {
    color: ({ color_theme }) => color_theme === 'LIGHT' ? 'black' : 'white', // default 'DARK'
    width: '100%',
    marginTop: '50px',
    lineHeight: '1.5',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center'
  }
});
